import React from 'react';
import CompanyCard from './CompanyCard';
import useElapsedTimer from '../hooks/useElapsedTimer';

export default function StationCard({
  station,
  session,
  company,
  isDragTarget,
  onDrop,
  onAdvanceStage,
  onViewDetails,
  workflowSteps,
  availableProctors,
  onAssignProctor,
  recommendation,
  onAssignRecommended
}) {
  const isOccupied = !!station.current_company_id;
  const elapsed = useElapsedTimer(session?.action_timestamp);
  const currentAction = session?.current_action || workflowSteps[0]?.key || 'dispatched';

  const statusLabels = {};
  workflowSteps.forEach((s) => {
    statusLabels[s.key] = s.label;
  });

  const getNextAction = () => {
    const currentIdx = workflowSteps.findIndex((s) => s.key === currentAction);
    const currentStep = workflowSteps[currentIdx];
    if (currentStep?.is_testing_step) {
      return { label: 'View Details', action: 'details' };
    }
    const nextStep = workflowSteps[currentIdx + 1];
    if (!nextStep) return null;
    return {
      label: nextStep.label,
      action: nextStep.key,
      is_testing_step: nextStep.is_testing_step
    };
  };

  const nextAction = getNextAction();
  const hasRecommendation = !isOccupied && !!recommendation;

  return (
    <div
      className={`station-card ${isOccupied ? 'occupied' : 'empty'}${hasRecommendation ? ' has-recommendation' : ''}`}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      style={isDragTarget && !isOccupied && !hasRecommendation ? { border: '3px dashed #28a745' } : {}}
    >
      <div className="station-title">
        <div className="station-number-badge">{station.station_number}</div>
        <div className="station-name-block">
          <div className="station-name-large">{station.name}</div>
          {station.station_pin && (
            <div className="station-pin" title="Station PIN">PIN: {station.station_pin}</div>
          )}
        </div>
      </div>

      <div className="station-body">
        {isOccupied && company ? (
          <>
            <CompanyCard company={company} />
            <div className="session-status">
              <span className={`status-badge status-${currentAction}`}>
                {statusLabels[currentAction] || currentAction}
              </span>
              <span className="status-timer">⏱ {elapsed}</span>
            </div>
            {nextAction && (
              <button
                className="btn advance-btn"
                onClick={() =>
                  nextAction.action === 'details'
                    ? onViewDetails()
                    : onAdvanceStage(nextAction.action)
                }
              >
                {nextAction.label} →
              </button>
            )}
          </>
        ) : hasRecommendation ? (
          <div className="station-recommendation">
            <div className="recommendation-label">Suggested Assignment</div>
            <div className="recommendation-company">{recommendation.companyName}</div>
            <button
              className="btn recommendation-btn"
              onClick={() => onAssignRecommended(station.id, recommendation.companyId)}
            >
              Assign →
            </button>
            <div className="recommendation-drop-hint">or drag a company here</div>
          </div>
        ) : (
          <div className="empty-station">
            <div className="empty-icon">📋</div>
            <div className="empty-text">Drop company here</div>
          </div>
        )}
      </div>

      <div className="station-footer">
        <select
          className="proctor-select"
          value={station.proctor_user_id || ''}
          onChange={(e) => onAssignProctor(station.id, e.target.value)}
        >
          <option value="">No Proctor</option>
          {availableProctors && availableProctors.map(p => (
            <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
